import React, { useMemo, useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import { motion } from "framer-motion";
import {
  ChevronDown,
  CircleCheck,
  EyeOff,
  FolderTree,
  Image as ImageIcon,
  Layers,
  LayoutGrid,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
} from "lucide-react";
import DeleteCategoryModal from "../../components/Categories/DeleteCategoryModal";
import CreateCategoryModal from "../../components/Categories/CreateCategoryModal";
import { fetchCategories } from "../../requests/Categories/CategoryService";
import EditCategoryModal from "../../components/Categories/EditCategoryModal";
import LoadErrorPlaceholder from "../../components/LoadErrorPlaceholder";

const categoryName = (item) => item?.name || item?.categoryName || "Untitled";

const firstImage = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
};

const categoryThumbnail = (item) =>
  firstImage(
    item?.thumbnail,
    item?.thumbnail_url,
    item?.thumb,
    item?.logo,
    item?.image,
    item?.image_url
  );

const parentKeyOf = (item) =>
  item?.category_id ??
  item?.parent_id ??
  item?.parentId ??
  item?.parent_category_id ??
  item?.parent?.id ??
  item?.category?.id ??
  null;

const isSubcategory = (item) => {
  if (parentKeyOf(item)) return true;
  const kind = String(
    item?.level || item?.category_level || item?.type || item?.categoryType || ""
  ).toLowerCase();
  return (
    kind === "subcategory" ||
    kind === "sub category" ||
    kind === "sub-category" ||
    kind === "child"
  );
};

const isInactiveCategory = (item) => {
  const raw = item?.status ?? item?.is_active ?? item?.isActive;
  if (raw === false || raw === 0) return true;
  const status = String(raw ?? "active").toLowerCase();
  return status === "inactive" || status === "hidden" || status === "0" || status === "false";
};

const isFeaturedCategory = (item) =>
  Boolean(item?.featured || item?.is_featured || item?.isFeatured);

const flattenCategories = (categories) => {
  const flat = [];
  const visit = (item, parentId) => {
    const embedded = item.subcategories || item.sub_categories || item.children;
    const node = { ...item };
    delete node.subcategories;
    delete node.sub_categories;
    delete node.children;
    if (parentId != null && parentKeyOf(node) == null) {
      node.parent_id = parentId;
    }
    flat.push(node);
    if (Array.isArray(embedded)) {
      embedded.forEach((child) => visit(child, item.id));
    }
  };
  (categories || []).forEach((item) => visit(item, null));
  return flat;
};

const byName = (a, b) =>
  categoryName(a).localeCompare(categoryName(b), undefined, { sensitivity: "base" });

const rowKey = (item) =>
  parentKeyOf(item) != null ? `subcategory-${item.id}` : `category-${item.id}`;

const buildCategoryTree = (categories) => {
  const roots = (Array.isArray(categories) ? categories : []).map((item) => {
    const embedded = item?.subcategories || item?.sub_categories || item?.children || [];
    const node = { ...item, children: [] };
    delete node.subcategories;
    delete node.sub_categories;
    node.children = (Array.isArray(embedded) ? embedded : []).map((child) => ({
      ...child,
      category_id: child?.category_id ?? item.id,
      children: [],
    }));
    return node;
  });

  const sortBranch = (node) => {
    node.children.sort(byName);
    node.children.forEach(sortBranch);
  };
  roots.sort(byName);
  roots.forEach(sortBranch);
  return roots;
};

const subcategoryCountLabel = (count) =>
  `${count} ${count === 1 ? "subcategory" : "subcategories"}`;

function CategoryMedia({ src, alt, className, iconSize = 14 }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        role="img"
        aria-label={alt}
        className={`inline-flex items-center justify-center shrink-0 bg-gray-200 text-gray-400 ${className}`}
      >
        <ImageIcon size={iconSize} strokeWidth={1.75} />
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className={`shrink-0 object-cover bg-gray-200 ${className}`}
    />
  );
}

function StatusPill({ inactive }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${
        inactive ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
      }`}
    >
      {inactive ? "Inactive" : "Active"}
    </span>
  );
}

function IconAction({ label, onClick, children, tone = "default" }) {
  const toneClass =
    tone === "danger"
      ? "hover:bg-red-50 hover:text-red-600"
      : tone === "brand"
      ? "hover:bg-[#FFF1F0] hover:text-[#FF6C6C]"
      : "hover:bg-gray-100 hover:text-gray-800";

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 transition-colors ${toneClass}`}
    >
      {children}
    </button>
  );
}

function Bone({ className }) {
  return <span className={`block animate-pulse rounded-md bg-gray-200 ${className}`} />;
}

function CategoryListSkeleton() {
  const nameWidths = ["w-36", "w-28", "w-44", "w-32", "w-40", "w-24"];

  return (
    <ul aria-busy="true" aria-label="Loading categories">
      {nameWidths.map((nameWidth, row) => (
        <li key={row} className="border-b border-gray-100 last:border-b-0">
          <div className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:px-4">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <Bone className="h-7 w-7 shrink-0 rounded-md" />
              <Bone className="h-8 w-8 shrink-0 rounded-md" />
              <Bone className={`h-3.5 ${nameWidth}`} />
            </div>
            <div className="flex flex-wrap items-center gap-1 pl-8 sm:justify-end sm:pl-0">
              <Bone className="h-5 w-28 rounded-full" />
              <Bone className="h-5 w-14 rounded-full" />
              <Bone className="h-7 w-7 rounded-md" />
              <Bone className="h-7 w-7 rounded-md" />
              <Bone className="h-7 w-7 rounded-md" />
              <Bone className="h-7 w-7 rounded-md" />
            </div>
          </div>
          {row === 1 && (
            <div className="px-3 pb-3 sm:px-4">
              <div className="ml-3 border-l-2 border-[#FFD0D0] pl-3 sm:ml-9">
                <Bone className="mt-1 h-2.5 w-36" />
                <ul className="mt-1.5 space-y-1.5">
                  {["w-24", "w-32"].map((childWidth) => (
                    <li
                      key={childWidth}
                      className="flex items-center gap-2 rounded-lg border border-dashed border-[#F3C1C1] bg-[#FFF8F7] px-3 py-2"
                    >
                      <Bone className="h-3 w-3 shrink-0" />
                      <Bone className="h-8 w-8 shrink-0 rounded-md" />
                      <div className="min-w-0 space-y-1.5">
                        <Bone className="h-2 w-16" />
                        <Bone className={`h-3 ${childWidth}`} />
                      </div>
                      <div className="ml-auto flex items-center gap-1.5">
                        <Bone className="h-5 w-14 rounded-full" />
                        <Bone className="h-7 w-7 rounded-md" />
                        <Bone className="h-7 w-7 rounded-md" />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

const Categories = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [listMode, setListMode] = useState("all");
  const [openMap, setOpenMap] = useState({});
  const [allOpen, setAllOpen] = useState(false);
  const [hiddenOverrides, setHiddenOverrides] = useState({});
  const [featuredOverrides, setFeaturedOverrides] = useState({});

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [createParent, setCreateParent] = useState(null);

  const refreshData = () => {
    setHiddenOverrides({});
    setFeaturedOverrides({});
    fetchCategories(setCategories, setLoading, setError);
  };

  useEffect(() => {
    refreshData();
  }, []);

  const tree = useMemo(() => buildCategoryTree(categories), [categories]);

  const isHidden = (item) =>
    Object.prototype.hasOwnProperty.call(hiddenOverrides, rowKey(item))
      ? hiddenOverrides[rowKey(item)]
      : isInactiveCategory(item);

  const isFeatured = (item) =>
    Object.prototype.hasOwnProperty.call(featuredOverrides, rowKey(item))
      ? featuredOverrides[rowKey(item)]
      : isFeaturedCategory(item);

  const stats = useMemo(() => {
    const flat = flattenCategories(categories);
    let parents = 0;
    let subcategories = 0;
    let active = 0;
    let inactive = 0;

    flat.forEach((item) => {
      if (isSubcategory(item)) subcategories += 1;
      else parents += 1;

      if (isInactiveCategory(item)) inactive += 1;
      else active += 1;
    });

    return {
      parents,
      subcategories,
      active,
      inactive,
      all: flat.length,
    };
  }, [categories]);

  const query = searchTerm.trim().toLowerCase();

  const visibleParents = useMemo(() => {
    const source =
      listMode === "parents" ? tree.filter((node) => !isSubcategory(node)) : tree;

    return source
      .map((parent) => {
        if (!query) return parent;

        const parentMatches = categoryName(parent).toLowerCase().includes(query);
        const matchingChildren = parent.children.filter((child) =>
          categoryName(child).toLowerCase().includes(query)
        );

        if (listMode === "parents") return parentMatches ? parent : null;
        if (parentMatches) return { ...parent, searchHit: true };
        if (matchingChildren.length) {
          return { ...parent, children: matchingChildren, searchHit: true };
        }
        return null;
      })
      .filter(Boolean);
  }, [tree, query, listMode]);

  const statCards = [
    {
      key: "parents",
      label: "Parent Categories",
      caption: "Top-level groups",
      value: stats.parents,
      icon: Layers,
      iconClass: "bg-slate-100 text-slate-600",
    },
    {
      key: "subcategories",
      label: "Sub Categories",
      caption: "Nested under a parent",
      value: stats.subcategories,
      icon: FolderTree,
      iconClass: "bg-[#FFF1F0] text-[#FF6C6C]",
    },
    {
      key: "active",
      label: "Active",
      caption: "Visible to shoppers",
      value: stats.active,
      icon: CircleCheck,
      iconClass: "bg-emerald-50 text-emerald-600",
    },
    {
      key: "inactive",
      label: "Inactive",
      caption: "Hidden from storefront",
      value: stats.inactive,
      icon: EyeOff,
      iconClass: "bg-amber-50 text-amber-600",
    },
  ];

  const everythingOpen = allOpen && Object.keys(openMap).length === 0;

  const isRowOpen = (parent) => {
    if (Object.prototype.hasOwnProperty.call(openMap, rowKey(parent))) return openMap[rowKey(parent)];
    if (parent.searchHit) return true;
    return allOpen;
  };

  const toggleRow = (parent) => {
    const nextOpen = !isRowOpen(parent);
    setOpenMap((current) => ({ ...current, [rowKey(parent)]: nextOpen }));
  };

  const toggleAll = () => {
    setOpenMap({});
    setAllOpen(!everythingOpen);
  };

  const openEdit = (item) => {
    setSelectedCategory(item);
    setEditModal(true);
  };

  const openDelete = (item) => {
    setSelectedCategory(item);
    setDeleteModal(true);
  };

  const sectionTitle = listMode === "parents" ? "Parents" : "All Categories + Subcategories";
  const sectionCopy =
    listMode === "parents"
      ? "Top-level parent categories. Switch to All Categories + Subcategories to open the nested subcategories."
      : "Open a parent category to see the subcategories nested under it.";

  return (
    <div>
      <Navbar page="Categories" />
      <div className="min-h-screen bg-[#F4F6F8]">
        <div className="w-full px-4 py-5 sm:px-6">
          <motion.header
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden bg-white border border-gray-200/80 shadow-sm rounded-2xl"
          >
            <div className="h-1 bg-[#FF6C6C]" />
            <div className="flex flex-col gap-5 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="flex items-start gap-4 min-w-0">
                <div className="flex items-center justify-center w-9 h-9 shrink-0 rounded-xl bg-[#FFF1F0] text-[#FF6C6C]">
                  <LayoutGrid size={18} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold tracking-[0.14em] text-[#FF6C6C]">
                    MARKETPLACE
                  </p>
                  <h1 className="mt-0.5 text-lg font-bold tracking-tight text-gray-900 text-balance">
                    Categories
                  </h1>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500 max-w-xl">
                    Open a parent category to manage the subcategories nested under it.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCreateParent(null);
                  setCreateModal(true);
                }}
                className="inline-flex items-center justify-center gap-1.5 self-start px-3 py-1.5 text-xs font-semibold text-white bg-[#FF6C6C] rounded-full shadow-sm shadow-[#FF6C6C]/25 hover:bg-[#ff5252] active:bg-[#e85a5a] transition-colors shrink-0"
              >
                <Plus size={16} strokeWidth={2.25} />
                New category
              </button>
            </div>
          </motion.header>

          <div className="grid grid-cols-1 gap-3 mt-4 sm:grid-cols-2 xl:grid-cols-4">
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.key}
                  className="flex items-center justify-between gap-3 px-4 py-3.5 bg-white border border-gray-200/80 shadow-sm rounded-2xl"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className={`flex items-center justify-center w-8 h-8 shrink-0 rounded-lg ${card.iconClass}`}
                    >
                      <Icon size={15} strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold leading-tight text-gray-900">
                        {card.label}
                      </p>
                      <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
                        {card.caption}
                      </p>
                    </div>
                  </div>
                  <p className="text-lg font-bold tabular-nums tracking-tight text-gray-900 shrink-0">
                    {loading ? "—" : card.value}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-4 bg-white border border-gray-200/80 shadow-sm rounded-2xl px-3 py-3 sm:px-4">
            <div className="flex flex-wrap gap-2">
              {[
                { id: "all", label: "All Categories + Subcategories", count: stats.all },
                { id: "parents", label: "Parents", count: stats.parents },
              ].map((filter) => {
                const selected = listMode === filter.id;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setListMode(filter.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                      selected
                        ? "bg-[#FF6C6C] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {filter.label}
                    <span className={`tabular-nums ${selected ? "text-white/85" : "text-gray-500"}`}>
                      {loading ? "—" : filter.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="relative mt-3">
              <Search
                size={16}
                className="absolute text-gray-400 -translate-y-1/2 left-3 top-1/2"
              />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search category name"
                className="w-full py-2 pl-9 pr-4 text-xs bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
              />
            </div>
          </div>

          <div className="mt-4 overflow-hidden bg-white border border-gray-200/80 shadow-sm rounded-2xl">
            <div className="flex items-start justify-between gap-3 px-4 py-4 border-b border-gray-100 sm:px-5">
              <div>
                <h2 className="text-xs font-semibold text-gray-900">{sectionTitle}</h2>
                <p className="mt-0.5 text-xs text-gray-500 max-w-xl">{sectionCopy}</p>
              </div>
              {listMode === "all" && (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="shrink-0 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  {everythingOpen ? "Collapse all" : "Expand all"}
                </button>
              )}
            </div>

            {loading ? (
              <CategoryListSkeleton />
            ) : error ? (
              <LoadErrorPlaceholder />
            ) : visibleParents.length === 0 ? (
              <p className="px-5 py-10 text-sm text-center text-gray-500">
                {query ? "No categories match that name." : "No categories yet."}
              </p>
            ) : (
              <ul>
                {visibleParents.map((parent) => {
                  const open = listMode === "all" && isRowOpen(parent);
                  const childCount = parent.children.length;
                  return (
                    <li key={rowKey(parent)} className="border-b border-gray-100 last:border-b-0">
                      <div
                        className={`flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:px-4 ${
                          listMode === "all" ? "cursor-pointer" : ""
                        }`}
                        onClick={() => {
                          if (listMode === "all") toggleRow(parent);
                        }}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          {listMode === "all" ? (
                            <button
                              type="button"
                              aria-expanded={open}
                              aria-label={open ? "Collapse subcategories" : "Expand subcategories"}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleRow(parent);
                              }}
                              className="inline-flex items-center justify-center w-7 h-7 text-gray-500 rounded-md hover:bg-gray-100"
                            >
                              <ChevronDown
                                size={15}
                                className={`transition-transform ${open ? "" : "-rotate-90"}`}
                              />
                            </button>
                          ) : (
                            <span className="w-7" />
                          )}
                          <CategoryMedia
                            src={categoryThumbnail(parent)}
                            alt={`${categoryName(parent)} thumbnail`}
                            iconSize={13}
                            className="w-8 h-8 rounded-md"
                          />
                          <p className="text-sm font-semibold text-gray-900 truncate">{categoryName(parent)}</p>
                        </div>

                        <div className="flex flex-wrap items-center gap-1 sm:justify-end pl-8 sm:pl-0">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setHiddenOverrides((current) => ({
                                ...current,
                                [rowKey(parent)]: !isHidden(parent),
                              }));
                            }}
                            className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border border-[#FFC7C7] bg-[#FFF6F6] text-[#E25555] hover:bg-[#FFECEC]"
                          >
                            {isHidden(parent) ? "Show" : "Hide"} · {subcategoryCountLabel(childCount)}
                          </button>
                          <StatusPill inactive={isHidden(parent)} />
                          <IconAction
                            label="Add subcategory"
                            tone="brand"
                            onClick={() => {
                              setCreateParent(parent);
                              setCreateModal(true);
                            }}
                          >
                            <Plus size={15} />
                          </IconAction>
                          <IconAction
                            label={isFeatured(parent) ? "Unfeature parent" : "Feature parent"}
                            onClick={() =>
                              setFeaturedOverrides((current) => ({
                                ...current,
                                [rowKey(parent)]: !isFeatured(parent),
                              }))
                            }
                          >
                            <Star
                              size={15}
                              className={isFeatured(parent) ? "text-amber-500 fill-amber-400" : ""}
                            />
                          </IconAction>
                          <IconAction label="Edit category" onClick={() => openEdit(parent)}>
                            <Pencil size={14} />
                          </IconAction>
                          <IconAction label="Delete category" tone="danger" onClick={() => openDelete(parent)}>
                            <Trash2 size={14} />
                          </IconAction>
                        </div>
                      </div>

                      {open && (
                        <div className="px-3 pb-3 sm:px-4">
                          <div className="ml-3 sm:ml-9 pl-3 border-l-2 border-[#FFD0D0]">
                            <p className="pt-0.5 text-[11px] font-semibold tracking-[0.12em] text-gray-400">
                              Nested under {categoryName(parent)}
                            </p>
                            {childCount === 0 ? (
                              <p className="mt-1.5 text-xs text-gray-500">
                                No subcategories under this parent yet.
                              </p>
                            ) : (
                              <ul className="mt-1.5 space-y-1.5">
                                {parent.children.map((child) => (
                                  <li
                                    key={rowKey(child)}
                                    className="flex flex-col gap-2 px-3 py-2 border border-dashed rounded-lg border-[#F3C1C1] bg-[#FFF8F7] sm:flex-row sm:items-center"
                                  >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span aria-hidden className="text-sm leading-none text-[#F0B4B4]">
                                        └
                                      </span>
                                      <CategoryMedia
                                        src={categoryThumbnail(child)}
                                        alt={`${categoryName(child)} thumbnail`}
                                        iconSize={13}
                                        className="w-8 h-8 rounded-md"
                                      />
                                      <div className="min-w-0">
                                        <p className="text-[11px] font-semibold tracking-[0.12em] text-[#FF6C6C]">
                                          Subcategory
                                        </p>
                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                          {categoryName(child)}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
                                      <StatusPill inactive={isHidden(child)} />
                                      {isFeatured(child) && (
                                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-full">
                                          Featured
                                        </span>
                                      )}
                                      <IconAction
                                        label={isFeatured(child) ? "Unfeature subcategory" : "Feature subcategory"}
                                        onClick={() =>
                                          setFeaturedOverrides((current) => ({
                                            ...current,
                                            [rowKey(child)]: !isFeatured(child),
                                          }))
                                        }
                                      >
                                        <Star
                                          size={15}
                                          className={isFeatured(child) ? "text-amber-500 fill-amber-400" : ""}
                                        />
                                      </IconAction>
                                      <IconAction label="Edit subcategory" onClick={() => openEdit(child)}>
                                        <Pencil size={14} />
                                      </IconAction>
                                      <IconAction
                                        label="Delete subcategory"
                                        tone="danger"
                                        onClick={() => openDelete(child)}
                                      >
                                        <Trash2 size={14} />
                                      </IconAction>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <CreateCategoryModal
        isOpen={createModal}
        onClose={() => {
          setCreateModal(false);
          setCreateParent(null);
        }}
        onSave={refreshData}
        parentCategory={createParent}
      />
      <EditCategoryModal
        isOpen={editModal}
        onClose={() => setEditModal(false)}
        category={selectedCategory}
        categories={categories}
        onSave={refreshData}
      />
      <DeleteCategoryModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        category={selectedCategory}
        onDelete={refreshData}
      />
    </div>
  );
};

export default Categories;
